// STEP STUDIO-OVERLAY-UX-FIX-1-STEP3 · 상품·사이즈표·QR 동시 표시 조합 (판매자 기본 설정 · 배치 좌표와 별도 저장).
//   · 저장: 실제 = 기존 PUT /seller/settings (overlay_default_show) · 테스트 = localStorage 만.
//   · 적용: 기존 useLiveOperations (toggleSizeTable · showQr · hideQr · applyPreset · hideOverlay) REUSE.
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import useLiveOperations from '@/hooks/useLiveOperations';
import { sizeTableHasData } from '@/components/live/OverlayExtras';

const TEST_KEY = 'livemoa_test_overlay_show';
export const DEFAULT_SHOW = { size_table: false, qr: false, qr_duration: 'persistent' };
export const QR_DURATIONS = [['persistent', '계속 표시'], [60, '60초'], [10, '10초']];
const norm = (v) => ({ ...DEFAULT_SHOW, ...(v && typeof v === 'object' ? v : {}) });
const qrSec = (d) => (d === 'persistent' ? 0 : Number(d) || 10);
const OFF = { size_table: false, qr: false, qr_duration: null };

const useOverlayShowCombo = ({ scheduleId, liveCtx, setLiveCtx, isLive, isTestMode }) => {
  const ops = useLiveOperations(scheduleId);
  const [prefs, setPrefs] = useState(() => {
    if (!isTestMode) return DEFAULT_SHOW;
    try { return norm(JSON.parse(localStorage.getItem(TEST_KEY) || 'null')); } catch { return DEFAULT_SHOW; }
  });
  const [loaded, setLoaded] = useState(isTestMode);
  useEffect(() => {
    if (isTestMode) return;
    api.get('/seller/settings').then(({ data }) => setPrefs(norm(data?.overlay_default_show)))
      .catch(() => {}).finally(() => setLoaded(true));
  }, [isTestMode]);

  const active = liveCtx?.active_product || null;
  const pid = active?.product_id || null;
  const display = liveCtx?.active_product_display || {};
  const productOn = !!pid && display.mode !== 'hidden';
  const stOn = !!display.size_table_visible;
  const qr = liveCtx?.active_product_qr || null;
  const qrOn = !!qr?.visible;
  const stData = sizeTableHasData(active?.size_table);
  const canApply = isLive && productOn;

  const applyLive = useCallback(async (next, prev) => {
    if (!canApply) return;
    const wantSt = !!next.size_table && stData;
    const qrChanged = next.qr !== prev.qr || (next.qr && next.qr_duration !== prev.qr_duration);
    if (isTestMode) {
      const sec = qrSec(next.qr_duration);
      setLiveCtx((c) => ({
        ...(c || {}),
        active_product_display: { ...((c || {}).active_product_display || {}), size_table_visible: wantSt },
        ...(qrChanged ? { active_product_qr: next.qr
          ? { visible: true, expires_at: sec ? new Date(Date.now() + sec * 1000).toISOString() : null }
          : { visible: false } } : {}),
      }));
      return;
    }
    try {
      if (wantSt !== stOn) await ops.toggleSizeTable(pid, display.preset || 'default', wantSt);
      if (qrChanged && next.qr) await ops.showQr(pid, qrSec(next.qr_duration));
      else if (qrChanged && qrOn) await ops.hideQr(pid);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Overlay 표시 변경 실패'); }
  }, [canApply, stData, isTestMode, setLiveCtx, stOn, qrOn, ops, pid, display.preset]);

  const save = useCallback((next) => {
    setPrefs(next);
    if (isTestMode) { try { localStorage.setItem(TEST_KEY, JSON.stringify(next)); } catch {} return; }
    api.put('/seller/settings', { overlay_default_show: next }).catch(() => toast.error('표시 설정 저장 실패'));
  }, [isTestMode]);

  const update = useCallback((patch) => {
    const next = { ...prefs, ...patch };
    save(next);
    applyLive(next, prefs);
  }, [prefs, save, applyLive]);

  const quick = useCallback((kind) => update({
    size_table: kind !== 'product', qr: kind === 'st_qr',
  }), [update]);

  const toggleProduct = useCallback(async () => {
    if (!isLive || !pid) return;
    if (isTestMode) {
      setLiveCtx((c) => {
        const d = (c || {}).active_product_display || {};
        const hide = d.mode !== 'hidden';
        return { ...(c || {}), active_product_display: hide
          ? { ...d, mode: 'hidden', size_table_visible: false }
          : { ...d, mode: 'visible', preset: d.preset && d.preset !== 'hidden' ? d.preset : 'default' } };
      });
      return;
    }
    try {
      if (productOn) await ops.hideOverlay(pid);
      else await ops.applyPreset(pid, display.preset && display.preset !== 'hidden' ? display.preset : 'default');
    } catch (e) { toast.error(e?.response?.data?.detail || '상품 카드 표시 변경 실패'); }
  }, [isLive, pid, isTestMode, setLiveCtx, productOn, ops, display.preset]);

  // 상품 변경 시 저장된 조합 자동 재적용 (상품당 1회).
  const appliedRef = useRef(null);
  useEffect(() => {
    if (!loaded || !canApply || appliedRef.current === pid) return;
    appliedRef.current = pid;
    if (prefs.size_table || prefs.qr) applyLive(prefs, OFF);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid, canApply, loaded]);

  // 테스트 모드 QR 60초/10초 자동 내림 (로컬).
  useEffect(() => {
    if (!isTestMode || !qr?.visible || !qr?.expires_at) return undefined;
    const ms = Date.parse(qr.expires_at) - Date.now();
    const id = setTimeout(() => setLiveCtx((c) => ({ ...(c || {}), active_product_qr: { visible: false } })), Math.max(0, ms));
    return () => clearTimeout(id);
  }, [isTestMode, qr?.visible, qr?.expires_at, setLiveCtx]);

  const pending = !isLive ? 'LIVE 시작 후 화면에 적용돼요 (설정은 저장됨)'
    : !pid ? '상품을 소개하면 화면에 적용돼요' : !productOn ? '상품 카드가 내려져 있어요' : null;
  const stReason = pending || (!stData ? '이 상품은 상품설명(사이즈·소재·기타)이 없어 화면에 표시되지 않아요 (상품 수정에서 등록)' : null);
  const qrReason = pending;

  return {
    prefs, update, quick, toggleProduct, productOn, productToggleable: isLive && !!pid, stOn, qrOn, stReason, qrReason, pending,
    combo: !!(prefs.size_table || prefs.qr),
  };
};

export default useOverlayShowCombo;
